import {HttpClient, HttpHeaders} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {AssetItem, Configuration, Hotspot, Page, PageMarker, Publication} from '@page2flip/core/common';
import {forkJoin, Observable, of, ReplaySubject, Subject, zip} from 'rxjs';
import {catchError, flatMap, map, switchMap} from 'rxjs/operators';

import {environment} from '../../../../../apps/creator/src/environments/environment';
import {ActivatedRoute, ParamMap} from '@angular/router';

/**
 * Service to fetch data from the backend.
 */
@Injectable()
export class DataService {
  private _selectedPageNumber: Subject<number> = new Subject<number>();
  public getSelectedPageNumber$: Observable<number> = this._selectedPageNumber.asObservable();
  private _loadedPublication: Publication;
  private _publicationHotspots: Hotspot[];
  private _publicationPages: Page[];
  private _viewerConfiguration: Configuration;
  private _project: any;
  private loadedPublicationId: string;
  private loadedProjectId: string;
  private publicationLanguage: string = 'de';
  private publicationLoadedSubject: ReplaySubject<string> = new ReplaySubject(1);
  private formatParameter: string = 'page2flip';
  private assetId: string = '';

  get viewerConfiguration(): Configuration {
    return this._viewerConfiguration;
  }

  get publicationHotspots(): Hotspot[] {
    return this._publicationHotspots;
  }

  set publicationHotspots(hotspots: Hotspot[]) {
    this._publicationHotspots = hotspots;
  }

  get publicationPages(): Page[] {
    return this._publicationPages;
  }

  get loadedPublication(): Publication {
    return this._loadedPublication;
  }

  get publicationLoaded(): Observable<string> {
    return this.publicationLoadedSubject.asObservable();
  }

  constructor(
    private httpClient: HttpClient,
    private route: ActivatedRoute
  ) {
    this.route.queryParamMap
      .subscribe((params: ParamMap) => {
        if (params.has('p')) {
          const publicationUrl: string = params.get('p');
          const splitFormat = publicationUrl?.split('/format/');
          if (splitFormat && splitFormat.length > 1) {
            this.formatParameter = splitFormat[1].split('/')[0];
          }
          const splitProject = publicationUrl.split('/project/');
          if (splitProject && splitProject.length > 1) {
            this.assetId = splitProject[1].split('/')[0];
          }
        }
      });
  }

  loadMediaAssets(mediaType): Observable<any> {
    if (mediaType === 'image' || mediaType === 'video') {
      return this.httpClient.get<AssetItem[]>(`${environment.spaceOneApiUrl}/scope/${environment.spaceOneScope}/assets`)
    } else {
      return this.httpClient
        .get<AssetItem[]>(`${environment.spaceOneApiUrl}/scope/${environment.spaceOneScope}/assets`)
        .pipe(
          flatMap(mediaAssets => {
            let calls = [];
            mediaAssets.map(asset => {
              if (asset.meta.itemType === mediaType || (mediaType === 'iframe' && asset.meta.itemType === 'zip')) {
                calls.push(
                  this.httpClient.get(`${environment.spaceOneApiUrl}/scope/${environment.spaceOneScope}/asset/${asset.id}`)
                );
              }
            });

            return zip(...calls);
          })
        );
    }
  }

  /**
   * Get all assetItems and filter to get Observable<Publication>.
   */
  loadPublications(): Observable<any> {
    return this.httpClient.get<AssetItem[]>(`${environment.spaceOneApiUrl}/scope/${environment.spaceOneScope}/assets`)
      .pipe(
        switchMap(assets => {
          const publications$: Observable<Publication>[] = [];
          assets.map(asset => {
            publications$.push(
              this.httpClient.get<Publication>(`${environment.spaceOneApiUrl}/scope/${environment.spaceOneScope}/asset/${asset.id}/format/${this.formatParameter}/content/data/publication.json`)
                .pipe(
                  map((response: Publication) => {
                    response.uuid = response.id.toString();
                    response.id = asset.id.toString();
                    return response;
                  }),
                  catchError(e => of(e))
                )
            );
          });

          return forkJoin(publications$);
        }),
        switchMap(responses => {
          const result: Publication[] = [];
          responses.map(response => {
            if (response.id) {
              result.push(response);
            }
          });

          return of(result);
        })
      );
  }

  async loadPublication(projectId: string, language?: string) {
    this.loadedProjectId = projectId;
    this.publicationLanguage = language;
    this._project = await this.getP2fProject(projectId).toPromise();
    if (this._project) {
      this.assetId = this._project.source;
      this._loadedPublication = this._project.details;
      this._viewerConfiguration = this._project.config;
    }
    this._publicationHotspots = await this.getP2fHotspots(projectId).toPromise();
    this._publicationPages = await this.getP2fPages(projectId).toPromise();
    this.publicationLoadedSubject.next(projectId);
  }

  getPublicationHotspots(projectId: string): Observable<Hotspot[]> {
    return this.getP2fHotspots(projectId);
  }

  getHotspot(hotspotId: string): Observable<Hotspot> {
    return this.httpClient.get<Hotspot>(`https://proxy.page2flip.de/api/scope/${environment.spaceOneScope}/asset/${this.loadedPublicationId}/format/${this.formatParameter}/page2flip/hotspot/${hotspotId}`);
  }

  updateHotspot(hotspot: Hotspot): Observable<Hotspot> {
    const url: string = `${environment.spaceOneApiUrl}/scope/${environment.spaceOneScope}/asset/${this.loadedPublicationId}/format/${this.formatParameter}/page2flip/hotspot/${hotspot.id}`;
    return this.httpClient.put<Hotspot>(url, hotspot);
  }

  deleteHotspot(hotspot: Hotspot): Observable<any> {
    return this.httpClient.delete(`${environment.spaceOneApiUrl}/scope/${environment.spaceOneScope}/asset/${this.loadedPublicationId}/format/${this.formatParameter}/page2flip/hotspot/${hotspot.id}`, {observe: 'response'});
  }

  unloadPublication() {
    delete this._loadedPublication;
    delete this._viewerConfiguration;
    delete this._publicationHotspots;
    delete this._publicationPages;
  }

  updatePublication(publication: Publication) {
    const patch = [];

    Object.entries(publication.meta).forEach(entry => {
      const key: string = entry[0];
      const value: string = entry[1];

      patch.push({
        op: 'replace',
        path: '/meta/' + key,
        value: value
      });
    });

    const id = publication.id || this.loadedPublicationId;
    const url: string = `${environment.spaceOneApiUrl}/scope/${environment.spaceOneScope}/asset/${id}/page2flip/page2flip/patchjson/data/publication.json`;
    const headers: HttpHeaders = new HttpHeaders({'Content-Type': 'application/json-patch+json'});

    return this.httpClient.patch(url, patch, {
      headers,
      observe: 'response',
      responseType: 'text'
    });
  }

  updatePublicationHotspots(publication: Publication, hotspots: Hotspot[]) {
    const id = publication.id || this.loadedPublicationId;
    const url: string = `${environment.spaceOneApiUrl}/scope/${environment.spaceOneScope}/asset/${this.assetId === '' ? id : this.assetId}/page2flip/${this.formatParameter}/patchhotspots`;
    const headers: HttpHeaders = new HttpHeaders({'Content-Type': 'application/json-patch+json'});

    return this.httpClient.patch(url, hotspots, {
      headers,
      observe: 'response',
      responseType: 'text'
    });
  }

  deletePublication(publication: Publication) {
    return this.httpClient.delete(`${environment.spaceOneApiUrl}/scope/${environment.spaceOneScope}/asset/${publication.id}`, {observe: 'response'});
  }

  publicationUrl(publicationId: string, file?: string): string {
    return `${environment.spaceOneApiUrl}/scope/${environment.spaceOneScope}/asset/${this.assetId === '' ? publicationId : this.assetId}/format/${this.formatParameter}/content/${file}`;
  }

  getPageUrl() {

  }

  projectUrl(projectId: string): string {
    return `${environment.spaceOneApiUrl}/scope/${environment.spaceOneScope}/project/${projectId}`;
  }

  getAssetThumbnail(id: number) {
    return `${environment.spaceOneApiUrl}/scope/${environment.spaceOneScope}/asset/${id}/thumbnail`;
  }

  publicationDownloadUrl(publicationId: string): string {
    return `${environment.spaceOneApiUrl}/scope/${environment.spaceOneScope}/asset/${publicationId}/format/${this.formatParameter}`;
  }

  wizardUrl(publicationId: number): string {
    return `/wizard/${environment.spaceOneInstance}/${environment.spaceOneScope}/${publicationId}`;
  }

  getSelectedPageNumber(pageNumber: number) {
    this._selectedPageNumber.next(pageNumber);
  }

  uploadActions(publicationId: string, actionsData: any) {
    const headers: HttpHeaders = new HttpHeaders(
      {
        'Content-Type': 'application/json'
      });

    let url: string = `${environment.spaceOneApiUrl}/scope/${environment.spaceOneScope}/asset/${publicationId}/actions`;

    return this.httpClient.post(url, actionsData, {
      headers,
      observe: 'response',
      responseType: 'json'
    });
  }

  loadActions(publicationId: string): Observable<any> {
    return this.httpClient.get(`${environment.spaceOneApiUrl}/scope/${environment.spaceOneScope}/asset/${publicationId}/actions`);
  }

  saveMarkers(publicationId: string, markers: any) {
    const headers: HttpHeaders = new HttpHeaders(
      {
        'Content-Type': 'application/json'
      });

    let url: string = `${environment.spaceOneApiUrl}/scope/${environment.spaceOneScope}/asset/${publicationId}/registertabs`;

    return this.httpClient.post(url, markers, {
      headers,
      observe: 'response',
      responseType: 'json'
    });
  }

  getMarkers(publicationId: string): Observable<any> {
    return this.httpClient.get(`${environment.spaceOneApiUrl}/scope/${environment.spaceOneScope}/asset/${publicationId}/registertabs`);
  }

  createPageMarkers(projectId: string, pageMarkers: PageMarker[], pageId: string) {
    const options = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json'
      }),
    };
    return this.httpClient.post<PageMarker[]>(`${environment.spaceOneApiUrl}/scope/${environment.spaceOneScope}/page2flip/project/${projectId}/page/${pageId}/markers`, pageMarkers, options);
  }

  deletePageMarkers(projectId: string, pageMarkerIds: string[], pageId: string) {
    const options = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json'
      }),
      body: JSON.stringify(pageMarkerIds)
    };
    return this.httpClient.delete<any>(
      `${environment.spaceOneApiUrl}/scope/${environment.spaceOneScope}/page2flip/project/${projectId}/page/${pageId}/markers`,
      options
    );
  }

  editPageMarkers(projectId: string, pageMarkers: PageMarker[], pageId: string) {
    const options = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json'
      }),
    };
    return this.httpClient.put<PageMarker[]>(`${environment.spaceOneApiUrl}/scope/${environment.spaceOneScope}/page2flip/project/${projectId}/page/${pageId}/markers`, pageMarkers, options);

  }

  updateConfigAutoplayInterval(publication: Publication, autoplayInterval: number) {
    const id = publication.id || this.loadedPublicationId;
    const url = `${environment.spaceOneApiUrl}/scope/${environment.spaceOneScope}/asset/${id}/page2flip/p2fdocument/patchjson/data/config.json`;
    const headers: HttpHeaders = new HttpHeaders({'Content-Type': 'application/json-patch+json'});

    return this.httpClient.patch(url, [{
        op: 'replace',
        path: 'autoplay',
        value: autoplayInterval
      }]
      , {
        headers,
        observe: 'response',
        responseType: 'text'
      });
  }

  updatePageMarkersConfiguration(projectId: string, value: boolean, key: string) {
    const headers: HttpHeaders = new HttpHeaders({'Content-Type': 'application/json-patch+json'});
    return this.httpClient.put<any>(
      `${environment.spaceOneApiUrl}/scope/${environment.spaceOneScope}/page2flip/project/${projectId}/markers/settings?${key}=${value}`, headers);
  }

  getArticleVisibilityConfiguration(): Observable<any> {
    return this.httpClient.get(`${environment.spaceOneApiUrl}/config/page2flip.creator.hotspotTypes.article`)
  }

  getP2fProject(projectId: string): Observable<any> {
    return this.httpClient.get(`${environment.spaceOneApiUrl}/scope/${environment.spaceOneScope}/page2flip/project/${projectId}`);
  }

  getP2fPages(projectId: string): Observable<Page[]> {
    return this.httpClient.get<Page[]>(`${environment.spaceOneApiUrl}/scope/${environment.spaceOneScope}/page2flip/project/${projectId}/pages`);
  }

  getP2fHotspots(projectId: string): Observable<Hotspot[]> {
    return this.httpClient.get<Hotspot[]>(`${environment.spaceOneApiUrl}/scope/${environment.spaceOneScope}/page2flip/project/${projectId}/hotspots`);
  }

  getP2fPage(projectId: string, id: string): string {
    return `${environment.spaceOneApiUrl}/scope/${environment.spaceOneScope}/page2flip/project/${projectId}/pages/${id}`
  }

  createHotspots(hotspots: Hotspot[], projectId: string): Observable<any> {
    return this.httpClient.post<Hotspot[]>(`${environment.spaceOneApiUrl}/scope/${environment.spaceOneScope}/page2flip/project/${projectId}/hotspots/batch`, hotspots);
  }


  deleteHotspots(hotspotIds: string[], projectId: string) {
    const options = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json'
      }),
      body: JSON.stringify(hotspotIds)
    };
    return this.httpClient.delete<any>(
      `${environment.spaceOneApiUrl}/scope/${environment.spaceOneScope}/page2flip/project/${projectId}/hotspots`,
      options
    );
  }

  editHotspots(hotspots: Hotspot[], projectId: string): Observable<Hotspot[]> {
    return this.httpClient.put<Hotspot[]>(
      `${environment.spaceOneApiUrl}/scope/${environment.spaceOneScope}/page2flip/project/${projectId}/hotspots`,
      hotspots
    );
  }
}
